import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Domain {
  id: string;
  name: string;
  symbol?: string;
  description?: string;
  channel_id?: string;
  industry_name?: string;
}

export interface DomainCategory {
  id: string;
  domain_id: string;
  name: string;
  description?: string;
}

export interface SimilarItem {
  id: string;
  name: string;
  similarity_score: number;
}

// Fetch all domains
export const useDomains = () => {
  return useQuery({
    queryKey: ['domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Domain[];
    }
  });
};

// Fetch domains filtered by industry
export const useDomainsByIndustry = (industry?: string) => {
  return useQuery({
    queryKey: ['domains', 'by-industry', industry],
    queryFn: async () => {
      if (!industry) return [];
      
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('industry_name', industry)
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as Domain[];
    },
    enabled: !!industry
  });
};

// Fetch categories for a specific domain
export const useDomainCategories = (domainId?: string | null) => {
  return useQuery({
    queryKey: ['domain_categories', domainId],
    queryFn: async () => {
      if (!domainId) return [];

      const { data, error } = await supabase
        .from('domain_categories')
        .select('*')
        .eq('domain_id', domainId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as DomainCategory[];
    },
    enabled: !!domainId
  });
};


// Find similar domains using fuzzy matching
export const useSimilarDomains = (searchText?: string) => {
  return useQuery({
    queryKey: ['similar_domains', searchText],
    queryFn: async () => {
      if (!searchText || searchText.length < 2) return [];

      const { data, error } = await supabase
        .rpc('find_similar_domains', { 
          search_text: searchText,
          threshold: 0.3 
        });

      if (error) throw error;
      return data as SimilarItem[];
    },
    enabled: !!searchText && searchText.length >= 2
  });
};

// Find similar categories using fuzzy matching
export const useSimilarCategories = (searchText?: string, domainId?: string) => {
  return useQuery({
    queryKey: ['similar_categories', searchText, domainId],
    queryFn: async () => {
      if (!searchText || searchText.length < 2 || !domainId) return [];

      const { data, error } = await supabase
        .rpc('find_similar_categories', { 
          search_text: searchText,
          domain_id_param: domainId,
          threshold: 0.3 
        });

      if (error) throw error;
      return data as SimilarItem[];
    },
    enabled: !!searchText && searchText.length >= 2 && !!domainId
  });
};


// Helper function to insert a new domain
export const insertDomain = async (name: string, description?: string, channelId?: string) => {
  const { data, error } = await supabase
    .from('domains')
    .insert({ name, description, channel_id: channelId })
    .select()
    .single();

  if (error) throw error;
  return data as Domain;
};

// Helper function to insert a new category
export const insertCategory = async (domainId: string, name: string, description?: string) => {
  const { data, error } = await supabase
    .from('domain_categories')
    .insert({ domain_id: domainId, name, description })
    .select()
    .single();

  if (error) throw error;
  return data as DomainCategory;
};

